import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Modal } from '@/shared/components/ui/Modal';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useShifts, useShiftActions } from '../hooks/useBiometricAdmin';
import type { BiometricShift } from '../biometricTypes';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BLANK: Partial<BiometricShift> = {
  name: '',
  start_time: '09:30',
  end_time: '18:30',
  crosses_midnight: false,
  grace_in_minutes: 10,
  grace_out_minutes: 10,
  half_day_after_minutes: 120,
  absent_after_minutes: 240,
  min_hours_full_day: 8,
  min_hours_half_day: 4,
  break_minutes: 60,
  break_auto_deduct: false,
  ot_starts_after_minutes: 30,
  ot_rounding_minutes: 15,
  ot_max_hours_per_day: 4,
  ot_multiplier: 1,
  weekly_offs: [0],
};

export function ShiftEditor() {
  const { data: shifts, isLoading } = useShifts();
  const { createShift, updateShift, deleteShift } = useShiftActions();
  const [editing, setEditing] = useState<Partial<BiometricShift> | null>(null);

  const set = <K extends keyof BiometricShift>(key: K, value: BiometricShift[K]) =>
    setEditing(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!editing?.name?.trim()) {
      toast.error('Give the shift a name.');
      return;
    }
    try {
      if (editing._id) {
        await updateShift.mutateAsync(editing as BiometricShift & { id: string });
        toast.success('Shift updated. Recompute affected days to apply it.');
      } else {
        await createShift.mutateAsync(editing);
        toast.success('Shift created.');
      }
      setEditing(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8"><Spinner /></div>;
  }

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">Shifts</h2>
          <p className="text-sm text-on-surface-variant">
            These rules replace eSSL&apos;s shift master. Changing one only affects days you recompute afterwards.
          </p>
        </div>
        <Button onClick={() => setEditing({ ...BLANK })}>
          <Plus className="mr-1 h-4 w-4" />
          New shift
        </Button>
      </div>

      {!shifts?.length ? (
        <p className="py-8 text-center text-sm text-on-surface-variant">
          No shifts yet. Create one — until then the engine uses a 09:30–18:30 default with Sunday off.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {shifts.map(shift => (
            <div key={shift._id} className="rounded-md border border-outline-variant p-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-on-surface">{shift.name}</h3>
                  <p className="font-mono text-sm text-on-surface-variant">
                    {shift.start_time} – {shift.end_time}
                    {shift.crosses_midnight && <Badge tone="info" className="ml-2">overnight</Badge>}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="rounded p-1 text-on-surface-variant hover:bg-surface-variant"
                    onClick={() => setEditing(shift)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-danger-text hover:bg-danger/10"
                    onClick={async () => {
                      if (!window.confirm(`Deactivate the "${shift.name}" shift?`)) return;
                      try {
                        await deleteShift.mutateAsync(shift._id);
                        toast.success('Shift deactivated.');
                      } catch (err) {
                        toast.error(getApiErrorMessage(err));
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <dl className="mt-2 space-y-0.5 text-xs text-on-surface-variant">
                <div>Grace {shift.grace_in_minutes}m in / {shift.grace_out_minutes}m out</div>
                <div>Full day {shift.min_hours_full_day}h · half day {shift.min_hours_half_day}h</div>
                <div>OT after {shift.ot_starts_after_minutes}m, rounded to {shift.ot_rounding_minutes}m</div>
                <div>Weekly off: {(shift.weekly_offs ?? []).map(d => DAYS[d]).join(', ') || 'none'}</div>
              </dl>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?._id ? `Edit ${editing.name}` : 'New shift'}
      >
        {editing && (
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
            <Row label="Name">
              <Input value={editing.name ?? ''} onChange={e => set('name', e.target.value)} />
            </Row>

            <div className="grid grid-cols-2 gap-3">
              <Row label="Start"><Input type="time" value={editing.start_time ?? ''} onChange={e => set('start_time', e.target.value)} /></Row>
              <Row label="End"><Input type="time" value={editing.end_time ?? ''} onChange={e => set('end_time', e.target.value)} /></Row>
            </div>

            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                checked={Boolean(editing.crosses_midnight)}
                onChange={e => set('crosses_midnight', e.target.checked)}
                className="h-4 w-4 rounded border-outline-variant"
              />
              Runs past midnight (night shift)
            </label>

            <div className="grid grid-cols-2 gap-3">
              <NumRow label="Grace in (min)" value={editing.grace_in_minutes} onChange={v => set('grace_in_minutes', v)} />
              <NumRow label="Grace out (min)" value={editing.grace_out_minutes} onChange={v => set('grace_out_minutes', v)} />
              <NumRow label="Half day after (min late)" value={editing.half_day_after_minutes} onChange={v => set('half_day_after_minutes', v)} />
              <NumRow label="Absent after (min late)" value={editing.absent_after_minutes} onChange={v => set('absent_after_minutes', v)} />
              <NumRow label="Full day (hours)" value={editing.min_hours_full_day} onChange={v => set('min_hours_full_day', v)} />
              <NumRow label="Half day (hours)" value={editing.min_hours_half_day} onChange={v => set('min_hours_half_day', v)} />
              <NumRow label="Break (min)" value={editing.break_minutes} onChange={v => set('break_minutes', v)} />
              <NumRow label="OT starts after (min)" value={editing.ot_starts_after_minutes} onChange={v => set('ot_starts_after_minutes', v)} />
              <NumRow label="OT rounding (min)" value={editing.ot_rounding_minutes} onChange={v => set('ot_rounding_minutes', v)} />
              <NumRow label="OT cap (hours/day)" value={editing.ot_max_hours_per_day} onChange={v => set('ot_max_hours_per_day', v)} />
              <NumRow label="OT multiplier" value={editing.ot_multiplier} onChange={v => set('ot_multiplier', v)} />
            </div>

            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                checked={Boolean(editing.break_auto_deduct)}
                onChange={e => set('break_auto_deduct', e.target.checked)}
                className="h-4 w-4 rounded border-outline-variant"
              />
              Deduct the break automatically (when there are no break punches)
            </label>

            <div>
              <span className="mb-1 block text-sm font-medium text-on-surface">Weekly offs</span>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day, index) => {
                  const selected = (editing.weekly_offs ?? []).includes(index);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        const current = editing.weekly_offs ?? [];
                        set('weekly_offs', selected ? current.filter(d => d !== index) : [...current, index]);
                      }}
                      className={`rounded-md border px-2 py-1 text-xs ${
                        selected
                          ? 'border-primary bg-primary-container text-on-primary-container'
                          : 'border-outline-variant text-on-surface-variant'
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save}>{editing._id ? 'Save changes' : 'Create shift'}</Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-on-surface">{label}</label>
      {children}
    </div>
  );
}

function NumRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: number;
  onChange: (value: number) => void;
}) {
  return (
    <Row label={label}>
      <Input type="number" value={value ?? 0} onChange={e => onChange(Number(e.target.value))} />
    </Row>
  );
}
